import React, { useEffect, useState } from 'react';
import { useBucket } from '@context/bucket.context';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { FORCE_SHOPPING_PREFS_MODAL_PATHNAME } from '@constants/user-shopping-preferences';
import { MEN_UNIVERSE, WOMEN_UNIVERSE } from '@lib/shopping-preferences/types';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import useWindowSize from '@hooks/use-window-size';
import Dialog from '@components/atoms/dialog/dialog';
import Icon from '@components/atoms/icon/icon';
import VcImage from '@components/atoms/vc-images/vc-image';
import { useShoppingPreferences } from '@hooks/user/use-shopping-preferences';
import { useShoppingPreferencesModal } from '@hooks/user/use-shopping-preferences-modal';
import clsx from 'clsx';
import styles from './user-shopping-preferences-modal.module.scss';

const OPTIONS = [
  {
    id: WOMEN_UNIVERSE,
    label: 'SHOPPING_PREFS.ONBOARDING_MODAL.WOMENSWEAR',
    imageUrl: '/images/shopping-preferences/1-womenswear-modal.jpg',
  },
  {
    id: MEN_UNIVERSE,
    label: 'SHOPPING_PREFS.ONBOARDING_MODAL.MENSWEAR',
    imageUrl: '/images/shopping-preferences/2-menswear-modal.jpg',
  },
];
interface ShoppingPreferenceModalProps {
  isFocusTrapDisabled?: boolean; // Only to prevent the focus trap to break the unit tests
}

export const ShoppingPreferenceModal: React.FC<ShoppingPreferenceModalProps> = (props) => {
  const { isFocusTrapDisabled = false } = props;
  const router = useRouter();
  const pathname = router?.asPath;
  const [triggerPrefsModal, setTriggerPrefsModal] = useState<boolean>(!!router?.query?.triggerPrefsModal);
  const { isWiderThanMd } = useWindowSize();
  const { isFeatureEnabled } = useBucket();
  const hideCloseIcon = isFeatureEnabled((f) => f?.shoppingPrefUndismissible, true) ?? false;
  const [focus, setFocus] = useState<number>(null);
  const [selected, setSelected] = useState<number>(WOMEN_UNIVERSE);
  const { preferences, updatePreferences, setSessionPreferences, hasUserSelectedPrefs } = useShoppingPreferences();
  const { showModal, setShowModal } = useShoppingPreferencesModal();
  const { t } = useTranslation('shopping-preferences');
  const { t: tA11y } = useTranslation('a11y');
  const { sendAnalyticEvent } = useAnalyticEvents('shopping_preferences');
  const [openModal, setOpenModal] = useState(triggerPrefsModal || showModal);
  const sendTracking = (event: string, action: string) => {
    sendAnalyticEvent(event, { action: action });
  };

  const handleSubmit = () => {
    if (selected == null) {
      return;
    }
    const newPrefs = {
      universe: selected,
      categories: [],
      custom: [],
    };

    sendTracking(
      'shopping_preferences_popin_click',
      `select_${selected === WOMEN_UNIVERSE ? 'womenswear' : 'menswear'}`,
    );

    // Use perm + session preferences for modal path, otherwise update normally
    if (pathname.startsWith(FORCE_SHOPPING_PREFS_MODAL_PATHNAME)) {
      setSessionPreferences(newPrefs);
      setTriggerPrefsModal(false);
    }

    updatePreferences(newPrefs); // Permanent storage + backend sync ([me/shopping-preferences])
    setShowModal(false);
  };

  useEffect(() => {
    setOpenModal(triggerPrefsModal ? triggerPrefsModal : showModal);
  }, [showModal, triggerPrefsModal]);

  useEffect(() => {
    if (preferences?.universe) {
      setSelected(preferences?.universe);
    }
  }, [preferences?.universe]);

  return (
    <Dialog
      isOpen={openModal}
      closeDialog={() => {
        setTriggerPrefsModal(false);
        setShowModal(false);
        if (!hasUserSelectedPrefs) {
          sendTracking('shopping_preferences_popin_close', 'close');
        }
      }}
      hasCloseIcon={!hideCloseIcon}
      hasCloseTxt={false}
      fullScreenOnMobile={false}
      styles={{
        overlay: {
          zIndex: '999999', // Temporary solution to display on top of the CMP popin
        },
        content: {
          maxWidth: '500px',
          width: '100%',
          flexDirection: 'column',
          inset: isWiderThanMd ? '50% auto auto 50%' : '100% auto auto 50%',
          transform: isWiderThanMd ? 'translate(-50%, -50%)' : 'translate(-50%, -100%)',
        },
      }}
      customClass={styles.shoppingPrefsModal}
      focusTrapActive={!isFocusTrapDisabled}
      onAfterOpen={() => sendTracking('shopping_preferences_popin_view', 'view')}
    >
      {(modalDialogTitleId) => (
        <div>
          <div
            className={styles.shoppingPrefsModal__title}
            id={modalDialogTitleId}
          >
            {t('SHOPPING_PREFS.ONBOARDING_MODAL.TITLE')}
          </div>
          <div className={styles.shoppingPrefsModal__subtitle}>{t('SHOPPING_PREFS.ONBOARDING_MODAL.TEXT')}</div>
          <div className={styles.shoppingPrefsModal__optionContainer}>
            {OPTIONS.map((option) => (
              <React.Fragment key={`${option.label}-${option.id}`}>
                <input
                  type="radio"
                  name={'shopping-preferences'}
                  id={`${option.id}-${option.label}`}
                  value={option.id === WOMEN_UNIVERSE ? 'womenswear' : 'meanswear'}
                  onChange={() => setSelected(option.id)}
                  onKeyUp={() => setFocus(option.id)}
                  className={clsx(styles.shoppingPrefsModal__input, 'visuallyhidden')}
                  data-cy={`shopping-prefs_modal_input-${option.id}`}
                  checked={selected === option.id}
                />
                <label
                  key={option.id}
                  className={clsx(
                    styles.shoppingPrefsModal__option,
                    selected === option.id ? styles['shoppingPrefsModal__option--selected'] : '',
                    focus === option.id ? styles['shoppingPrefsModal__option--focus'] : '',
                  )}
                  htmlFor={`${option.id}-${option.label}`}
                  tabIndex={-1}
                  data-cy={`shopping-prefs_modal_label-${option.id}`}
                  aria-label={
                    option.id === WOMEN_UNIVERSE
                      ? t('A11Y.SHOPPING_PREFS.LABEL.WOMENSWEAR')
                      : t('A11Y.SHOPPING_PREFS.LABEL.MENSWEAR')
                  }
                >
                  <VcImage
                    src={option.imageUrl}
                    alt=""
                    width={72}
                    height={72}
                    data-cy={`shopping-prefs_modal_image-${option.id}`}
                    shouldUseAssetImage
                    wrapperProps={{ className: styles.shoppingPrefsModal__option__image }}
                  />
                  <span className={styles.shoppingPrefsModal__option__label}>{t(option.label)}</span>
                  <Icon
                    name="tick-default"
                    className={styles.shoppingPrefsModal__option__icon}
                    ariaLabel={tA11y('A11Y.ICON.TICK_SELECTED')}
                    data-cy={`shopping-prefs_modal_tick-${option.id}`}
                  />
                </label>
              </React.Fragment>
            ))}
          </div>

          <button
            type="button"
            className="vc-btn vc-btn--primary vc-btn--large vc-btn--full"
            onClick={handleSubmit}
            disabled={selected == null}
            data-cy="shopping-prefs_modal_CTA"
          >
            {t('SHOPPING_PREFS.ONBOARDING_MODAL.CTA')}
          </button>
        </div>
      )}
    </Dialog>
  );
};
